import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BusinessService } from '../../../services/business';
import { Service, Professional } from '../../../shared/interfaces';
import { BaseAuthComponent } from '../../../shared/common/BaseAuthComponent.class';
import { ToastService } from '../../../shared/services';
import {
  DurationOption,
  SERVICE_DURATIONS,
} from '../../../config/serviceDurations';
import { FieldLabels } from '../../../shared/common/formValidHelper';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { REQUIRE_AUTH } from '../../../shared/interceptors/auth-context';

@Component({
  selector: 'app-service-add',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './service-add.html',
  styleUrl: './service-add.css',
})
export class ServiceAdd extends BaseAuthComponent implements OnInit {
  serviceForm: FormGroup;
  @Input() entityId: string | null = null;
  @Input() endpoint: string | null = null;
  businessService = inject(BusinessService);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  isLoadingData = signal<boolean>(false);
  durationOptions: DurationOption[] = SERVICE_DURATIONS;
  professionals = signal<Professional[]>([]);
  filteredProfessionals = signal<Professional[]>([]);
  professionalSearchTerm = signal<string>('');
  assignedProfessionalIds = signal<string[]>([]);
  serviceImagePreview = signal<string | null>(null);
  private serviceImageFile: File | null = null;
  private priceFormatter = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
  });
  private defaultIntervalDays = 7;
  private apiBaseUrl = environment.apiUrl;

  constructor(private fb: FormBuilder) {
    super();
    this.serviceForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      price: ['', Validators.required],
      durationMinutes: ['', Validators.required],
      hasSessions: [false],
      sessionsCount: [{ value: '', disabled: true }],
      sessionIntervalDays: [{ value: '', disabled: true }],
      professionalIds: [[] as string[]],
    });

    this.serviceForm
      .get('hasSessions')
      ?.valueChanges.subscribe((enabled: boolean) => {
        this.toggleSessionControls(!!enabled);
      });
  }

  ngOnInit(): void {
    this.loadProfessionals();
    if (this.entityId && this.endpoint) {
      this.loadEntity(this.entityId);
    }
  }

  loadProfessionals() {
    this.businessService
      .searchEntityByBusinessId(this.businessId, 'professionals', '', 1, 100)
      .subscribe({
        next: (list) => {
          this.professionals.set(list as Professional[]);
          this.filteredProfessionals.set(list as Professional[]);
        },
        error: (err) => {
          console.error('Error loading professionals:', err);
        },
      });
  }

  onProfessionalSearch(term: string) {
    this.professionalSearchTerm.set(term);
    const lower = term.toLowerCase().trim();
    if (!lower) {
      this.filteredProfessionals.set(this.professionals());
      return;
    }
    this.filteredProfessionals.set(
      this.professionals().filter((p) => {
        const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
        const email = (p.email || '').toLowerCase();
        return name.includes(lower) || email.includes(lower);
      })
    );
  }

  getInitial(professional: Professional): string {
    return (professional.firstName?.[0] || professional.email?.[0] || '?').toUpperCase();
  }

  get selectedCount(): number {
    return this.assignedProfessionalIds().length;
  }

  loadServiceProfessionals(serviceId: string) {
    this.http
      .get<Professional[]>(
        `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${serviceId}/professionals`,
        {
          context: new HttpContext().set(REQUIRE_AUTH, true),
        }
      )
      .subscribe({
        next: (professionals) => {
          const ids = professionals.map((p) => p.userId);
          this.assignedProfessionalIds.set(ids);
          this.serviceForm.patchValue({ professionalIds: ids });
        },
        error: (err) => {
          console.error('Error loading service professionals:', err);
        },
      });
  }

  loadEntity(entityId: string) {
    this.isLoadingData.set(true);
    this.businessService
      .getRecordByBusinessId<Service>(this.businessId, entityId, this.endpoint!)
      .subscribe({
        next: (res) => {
          this.mapServiceToForm(res);
          this.loadServiceProfessionals(entityId);
          this.isLoadingData.set(false);
        },
        error: (err) => {
          console.error(err);
          this.isLoadingData.set(false);
        },
      });
  }

  private mapServiceToForm(service: Service) {
    this.serviceForm.patchValue({
      name: service.name,
      description: service.description,
      price: this.formatPrice(service.price),
      durationMinutes: service.durationMinutes,
      hasSessions: !!service.hasSessions,
      sessionsCount: service.sessionsCount ?? '',
      sessionIntervalDays: service.sessionIntervalDays ?? '',
    });

    this.serviceImagePreview.set((service as any).imageUrl ?? null);
    this.serviceImageFile = null;
    this.toggleSessionControls(!!service.hasSessions);
  }

  isValid(): boolean {
    const fieldLabels: FieldLabels = {
      name: 'Nombre',
      price: 'Precio',
      durationMinutes: 'Duración del Servicio',
      sessionsCount: 'Número de sesiones',
      sessionIntervalDays: 'Intervalo de días entre sesiones',
    };
    this.toastFormErrors('crear el servicio', this.serviceForm, fieldLabels);
    this.serviceForm.markAllAsTouched();
    return this.serviceForm.valid;
  }

  getValue(): any {
    const { name, description, price, durationMinutes, professionalIds } =
      this.serviceForm.getRawValue();
    const parsedPrice = this.parsePrice(price);

    return {
      name: name,
      description: description,
      price: parsedPrice,
      durationMinutes: durationMinutes,
      hasSessions: !!this.serviceForm.get('hasSessions')?.value,
      sessionsCount: this.serviceForm.get('hasSessions')?.value
        ? Number(this.serviceForm.get('sessionsCount')?.value ?? 0)
        : null,
      sessionIntervalDays: this.serviceForm.get('hasSessions')?.value
        ? Number(this.serviceForm.get('sessionIntervalDays')?.value ?? 0)
        : null,
      entityId: this.entityId,
      professionalIds: professionalIds || [],
    };
  }

  getProfessionalDisplayName(professional: Professional): string {
    if (professional.firstName || professional.lastName) {
      return `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
    }
    return professional.email || 'Profesional';
  }

  isProfessionalSelected(professionalId: string): boolean {
    return this.assignedProfessionalIds().includes(professionalId);
  }

  toggleProfessional(professionalId: string) {
    const currentIds = this.assignedProfessionalIds();
    const index = currentIds.indexOf(professionalId);

    if (index > -1) {
      // Remove
      const newIds = currentIds.filter(id => id !== professionalId);
      this.assignedProfessionalIds.set(newIds);
      this.serviceForm.patchValue({ professionalIds: newIds });
    } else {
      // Add
      const newIds = [...currentIds, professionalId];
      this.assignedProfessionalIds.set(newIds);
      this.serviceForm.patchValue({ professionalIds: newIds });
    }
  }

  onPriceInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const formatted = digits ? this.formatPrice(Number(digits)) : '';
    input.value = formatted;
    (this.serviceForm.get('price') as FormControl).setValue(formatted, {
      emitEvent: false,
    });
  }

  private parsePrice(value: string | number | null): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const digits = value.replace(/\D/g, '');
    return digits ? Number(digits) : 0;
  }

  private formatPrice(value: string | number | null): string {
    if (value === null || value === undefined || value === '') return '';
    const numeric =
      typeof value === 'number' ? value : Number(value.toString().replace(/\D/g, ''));
    if (!numeric) return '';
    return this.priceFormatter.format(numeric);
  }

  private toggleSessionControls(enabled: boolean) {
    const sessionsCountCtrl = this.serviceForm.get('sessionsCount');
    const intervalCtrl = this.serviceForm.get('sessionIntervalDays');

    if (!sessionsCountCtrl || !intervalCtrl) return;

    if (enabled) {
        sessionsCountCtrl.enable({ emitEvent: false });
        intervalCtrl.enable({ emitEvent: false });
        sessionsCountCtrl.setValidators([Validators.required, Validators.min(2)]);
        intervalCtrl.setValidators([Validators.required, Validators.min(1)]);
        if (!sessionsCountCtrl.value) {
          sessionsCountCtrl.setValue(2, { emitEvent: false });
        }
        if (!intervalCtrl.value) {
          intervalCtrl.setValue(this.defaultIntervalDays, { emitEvent: false });
        }
    } else {
        sessionsCountCtrl.reset({ value: '', disabled: true }, { emitEvent: false });
        intervalCtrl.reset({ value: '', disabled: true }, { emitEvent: false });
        sessionsCountCtrl.clearValidators();
        intervalCtrl.clearValidators();
    }

    sessionsCountCtrl.updateValueAndValidity({ emitEvent: false });
    intervalCtrl.updateValueAndValidity({ emitEvent: false });
  }

  onServiceImageSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.show('Solo se permiten imágenes JPEG, PNG, JPG y WebP', 'warning');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.toastService.show('El tamaño máximo de la imagen es 5MB', 'warning');
      return;
    }

    this.serviceImageFile = file;
    const reader = new FileReader();
    reader.onload = () => this.serviceImagePreview.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  clearServiceImage(): void {
    this.serviceImageFile = null;
    const currentImage = (this.serviceForm.getRawValue() as any).imageUrl ?? null;
    this.serviceImagePreview.set(currentImage);
  }

  getServiceImageFile(): File | null {
    return this.serviceImageFile;
  }
}
